<template>
  <div v-if="loading" class="alert alert-info">Loading prices...</div>
  <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
  <div v-else class="table-responsive">
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>ID</th>
          <th>Name</th>
          <th>Vintage</th>
          <th>Bid</th>
          <th>Ask</th>
          <th>Source</th>
          <th>Date</th>
          <th>Created</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="filteredPrices.length === 0">
          <td colspan="9" class="text-center">No price history found.</td>
        </tr>
        <tr v-for="price in filteredPrices" :key="price.id">
          <td>{{ price.id }}</td>
          <td>{{ price.name }}</td>
          <td>{{ price.vintage ?? "N/A" }}</td>
          <td>
            {{ formatPrice(price.bid, price.currency, price.unit) }}
          </td>
          <td>
            {{ formatPrice(price.ask, price.currency, price.unit) }}
          </td>
          <td>{{ price.source }}</td>
          <td>{{ formatPricingDate(price.date) }}</td>
          <td>{{ formatPricingDateTime(price.created_at) }}</td>
          <td>
            <button
              class="btn btn-sm btn-danger"
              @click="handleDelete(price.id)"
              :disabled="deletingIds.has(price.id)"
              title="Delete price"
            >
              <span
                v-if="deletingIds.has(price.id)"
                class="spinner-border spinner-border-sm me-1"
              ></span>
              <i v-else class="fas fa-trash me-1"></i>
              Delete
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import type { EacPrice } from "@/market_insights/admin/eacPricingTypes";
import {
  formatPricingDate,
  formatPricingDateTime,
  getCurrencySymbol,
} from "@/market_insights/admin/eacPricingFormat";

defineProps<{
  filteredPrices: EacPrice[];
  loading: boolean;
  error: string | null;
  deletingIds: Set<number>;
}>();

const emit = defineEmits<{
  "price-deleted": [];
  "delete-start": [id: number];
  "delete-end": [id: number];
}>();

// Global handlers
const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();

/**
 * Format price with currency symbol and unit
 *
 * @param price - Price value as string, null, or undefined
 * @param currency - Currency code (e.g., 'eur', 'usd') or null
 * @param unit - Unit of measurement (e.g., 'MWh', 'MT') or null
 * @returns Formatted price string or empty string if price is missing
 */
const formatPrice = (
  price: string | null | undefined,
  currency: string | null,
  unit: string | null
): string => {
  if (!price || price === "None" || price === "null") {
    return "";
  }

  if (!currency || !unit) {
    return price;
  }

  const symbol = getCurrencySymbol(currency);
  return `${symbol}${price}/${unit}`;
};

/**
 * Handle deletion of a price entry
 *
 * @param priceId - ID of the price to delete
 */
const handleDelete = async (priceId: number): Promise<void> => {
  if (!confirm("Are you sure you want to delete this price entry?")) {
    return;
  }

  emit("delete-start", priceId);
  try {
    await goSolidApi.delete(`/api/admin/eac-pricing/prices/${priceId}`);
    globalSuccessHandler("Success", "EAC price deleted successfully");
    emit("price-deleted");
  } catch (error: any) {
    globalErrorHandler(error);
  } finally {
    emit("delete-end", priceId);
  }
};
</script>

<style lang="scss" scoped>
.table-responsive {
  max-height: 70vh;
}
</style>
